import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { pulseMultipartUpload } from '../uploadService';
import logger from '../../lib/logger';
import { RootState } from '../store';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/**
 * Sends a heartbeat (pulse) to the backend every 5 minutes while the recorder tab
 * is open (recording OR paused). Clears the interval once the upload is
 * finalized, fails, or the component using this hook unmounts.
 */
export default function useUploadPulse() {
  const { status, upload } = useSelector((state: RootState) => state.recorder);
  const { teamId, folderId, fileId } = upload;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const sendPulse = useCallback(async () => {
    if (!fileId || !teamId || !folderId) return;

    try {
      await pulseMultipartUpload({ teamId, folderId, fileId });
    } catch (error) {
      logger.error('Pulse request failed', error);
      // Retry once after 10 s, ignore result
      setTimeout(async () => {
        try {
          await pulseMultipartUpload({ teamId, folderId, fileId });
        } catch (retryError) {
          logger.error('Pulse retry failed', retryError);
        }
      }, 10_000);
    }
  }, [fileId, teamId, folderId]);

  useEffect(() => {
    const isActive =
      (status === 'recording' || status === 'paused') &&
      fileId &&
      teamId &&
      folderId;

    if (isActive && !intervalRef.current) {
      // Send first pulse immediately so backend is aware quickly
      sendPulse();
      intervalRef.current = setInterval(sendPulse, FIVE_MINUTES_MS);
    }

    // Clear interval when recording finishes / fails or component unmounts
    if (!isActive && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [status, fileId, teamId, folderId, sendPulse]);
}
